import Loading from "components/icons/Loading"
import { Suspense, useEffect, useRef, useState } from "react"
import Image from "next/image"
import { useUserContext } from "context/UserContext"
import { useLang } from "hooks/useLang"
import { FiUser } from "react-icons/fi"

const UserAvatar: React.FC = () => {
    const { t } = useLang()
    const ref = useRef<HTMLInputElement>(null)
    const { user, uploadAvatar } = useUserContext()
    const [imageError, setImageError] = useState<boolean>(false)
    const [imageLoadError, setImageLoadError] = useState<boolean>(false)

    const { mutate, isSuccess, isLoading, data } = uploadAvatar

    const imageURL = user?.image
        ? `${process.env.NEXT_PUBLIC_Event_Img_URL}${user?.image}`
        : undefined

    const handleImageClick = () => ref.current?.click()

    const handleChangeImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        setImageError(false)
        setImageLoadError(false)

        const file = e.target.files?.[0]
        if (file) {
            const formData = new FormData()
            formData.append("avatar", file)
            mutate(formData)
        }
    }

    const handleImageError = () => {
        setImageLoadError(true)
    }

    useEffect(() => {
        if (data) {
            const hasServerError = data.status === 500
            const successfulImageUpload = isSuccess && data?.payload?.image

            setImageError(hasServerError)
            if (successfulImageUpload) {
                setImageError(false)
                setImageLoadError(false)
            }
        }
    }, [isSuccess, data])

    const errorMessage = (message: string) => {
        switch (message) {
            case "error on upload from user avatar":
                return t("invalid_image_file")
            case "File too large":
                return t("invalid_image_size")
            default:
                return t("invalid_image")
        }
    }

    const renderAvatarContent = () => {
        if (isLoading) {
            return <Loading />
        }

        if (!imageURL || imageLoadError) {
            return (
                <div className="flex h-full w-full items-center justify-center bg-secondary-theme-400 hover:opacity-80">
                    <FiUser className="h-1/2 w-1/2" />
                </div>
            )
        }

        return (
            <Suspense fallback={<Loading />}>
                <Image
                    src={imageURL}
                    alt="Profile-image"
                    fill
                    onError={handleImageError}
                    style={{
                        objectFit: "cover",
                        objectPosition: "center center",
                    }}
                />
            </Suspense>
        )
    }

    return (
        <div className="flex flex-col items-center justify-center gap-2 pb-5 pt-5 md:flex-row md:justify-start md:gap-5 md:pt-0">
            <div className="flex flex-col items-center">
                <div
                    onClick={handleImageClick}
                    className={`relative flex h-[70px] w-[70px] cursor-pointer items-center justify-center overflow-hidden rounded-full bg-secondary-theme-400 dark:bg-[#33276F] md:h-[80px] md:w-[80px] ${
                        imageError ? "errorStyle" : ""
                    }`}
                >
                    {renderAvatarContent()}
                    <input
                        onChange={handleChangeImage}
                        className="hidden"
                        ref={ref}
                        type="file"
                        accept="image/*"
                        multiple={false}
                    />
                </div>
                {imageError && (
                    <div className="mt-2 h-5 w-full text-xs font-medium text-red-500">
                        {errorMessage(data?.message || "")}
                    </div>
                )}
            </div>
            <p className="flex text-base font-medium md:text-xl">
                {user?.first_name} {user?.last_name}
            </p>
        </div>
    )
}

export default UserAvatar
